import { CSSProperties, FC, ReactNode } from "react";

import styled from "@emotion/styled";

const RING_MASK =
  "radial-gradient(circle farthest-side, transparent calc(100% - var(--ring-stroke)), #000 calc(100% - var(--ring-stroke)))";

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  width: var(--ring-size);
  height: var(--ring-size);
`;

const Track = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: var(--ring-track);
  -webkit-mask: ${RING_MASK};
  mask: ${RING_MASK};
`;

const Arc = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: var(--ring-arc);
  -webkit-mask: ${RING_MASK};
  mask: ${RING_MASK};
`;

const Cap = styled.span`
  position: absolute;
  left: var(--cap-x);
  top: var(--cap-y);
  width: var(--ring-stroke);
  height: var(--ring-stroke);
  border-radius: 50%;
  background: var(--cap-color);
  transform: translate(-50%, -50%);
`;

const Center = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export interface RingProps {
  progress: number;
  size?: number;
  strokeWidth?: number;
  trackColor?: string;
  progressColor?: string;
  /**
   * Colors for a gradient progress arc, running along the arc from its start
   * (top) to its end. When set, this takes precedence over `progressColor`.
   * Stops are spread evenly across the filled portion.
   */
  progressGradient?: string[];
  children?: ReactNode;
  className?: string;
  "aria-label"?: string;
}

const Ring: FC<RingProps> = ({
  progress,
  size = 80,
  strokeWidth = 6,
  trackColor = "var(--color-gray-lighter)",
  progressColor = "var(--color-charcoal)",
  progressGradient,
  children,
  className,
  "aria-label": ariaLabel
}) => {
  const clamped = Math.min(Math.max(progress, 0), 1);
  const sweep = clamped * 360;
  const hasGradient = progressGradient != null && progressGradient.length > 0;
  const colors = hasGradient ? progressGradient : [progressColor];

  const stops =
    colors.length === 1
      ? [`${colors[0]} 0deg`, `${colors[0]} ${sweep.toFixed(2)}deg`]
      : colors.map(
          (color, index) =>
            `${color} ${((index / (colors.length - 1)) * sweep).toFixed(2)}deg`
        );
  const arcImage =
    clamped >= 1
      ? `conic-gradient(from 0deg, ${stops.join(", ")})`
      : `conic-gradient(from 0deg, ${stops.join(", ")}, transparent ${sweep.toFixed(
          2
        )}deg)`;

  const centerlineRadius = (size - strokeWidth) / 2;
  const capAt = (angleDeg: number) => {
    const rad = (angleDeg * Math.PI) / 180;
    return {
      x: size / 2 + centerlineRadius * Math.sin(rad),
      y: size / 2 - centerlineRadius * Math.cos(rad)
    };
  };
  const showCaps = clamped > 0 && clamped < 1;
  const startCap = capAt(0);
  const endCap = capAt(sweep);

  const wrapperStyle = {
    "--ring-size": `${size}px`,
    "--ring-stroke": `${strokeWidth}px`,
    "--ring-track": trackColor,
    "--ring-arc": arcImage
  } as CSSProperties;

  const startCapStyle = {
    "--cap-x": `${startCap.x}px`,
    "--cap-y": `${startCap.y}px`,
    "--cap-color": colors[0]
  } as CSSProperties;

  const endCapStyle = {
    "--cap-x": `${endCap.x}px`,
    "--cap-y": `${endCap.y}px`,
    "--cap-color": colors[colors.length - 1]
  } as CSSProperties;

  return (
    <Wrapper
      style={wrapperStyle}
      className={className}
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel}
    >
      <Track />
      <Arc />
      {showCaps && (
        <>
          <Cap style={startCapStyle} />
          <Cap style={endCapStyle} />
        </>
      )}
      {children != null && <Center>{children}</Center>}
    </Wrapper>
  );
};

export default Ring;
